import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { getIconComponent } from "@/Utils/getIconComponent";
import FormLabel from "./FormLabel";
import { LuChevronDown } from "react-icons/lu";

export default React.memo(function InputDropdown({
  label,
  placeholder,
  options = [],
  value = "",
  onChange,
  icon = null,
  dropdownIconName = null,
  startIconName = null, // NEW PROP
  endIconName = null, // NEW PROP
  iconColor = "var(--gray-400)", // default color
  sx,
  error = false,
  helperText = "",
  width,
  height,
  gap,
  disabled,
  endpoint,
  required,
  renderValue,
  loading = false,
  ...props
}) {
  const [fetchedOptions, setFetchedOptions] = useState(options);
  useEffect(() => {
    setFetchedOptions(options);
  }, []);

  const [open, setOpen] = useState(false); // ✅ controls open state
  const isPlaceholder = value === "";
  const renderIcon = (iconName) =>
    iconName
      ? getIconComponent(iconName, {
          color: iconColor,
          size: 24,
        })
      : null;

  return (
    <Box
      gap={label ? 1 : 0}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...sx,
        width: width ? `${width} !important` : "100% !important",
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <FormLabel
        label={label}
        gap={gap}
        required={required}
        disabled={disabled}
      />
      <TextField
        select
        disabled={disabled}
        
        size="small"
        value={
          typeof value === "object"
            ? value?.value || value?.name || ""
            : value || ""
        }
        onChange={(eOrValue) =>
          onChange?.(eOrValue?.target ? eOrValue.target.value : eOrValue)
        }
        error={error}
        helperText={helperText}
        sx={{
          ...sx,
          // opacity: disabled ? 0.6 : 1, // faded when disabled
          height: height,
          "& .MuiOutlinedInput-root": {
            fontSize: "16px",
            height: sx?.height || "auto",
            backgroundColor: disabled ? "var(--gray-50)" : "var(--gray-0)", // bg change when disabled
            "& .MuiSelect-select": {
              height: `${sx?.inputHeight || 40}px !important`,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
              padding: 2,
            },
            "& fieldset": {
              border: error
                ? "1px solid var(--red-900) !important"
                : "1px solid var(--gray-300)",
              borderRadius: "8px",
            },
            "&.Mui-focused fieldset": {
              border: "1px solid",
              borderColor: "var(--brand-600) !important",
              boxShadow: "0 0 0 4px var(--brand-200)",
            },
            "&:hover fieldset": {
              borderColor: "var(--gray-300)",
            },
            fontWeight: isPlaceholder ? 400 : 500,
            color: isPlaceholder ? "var(--gray-500)" : "var(--brand-900)",
          },
          "& .MuiFormHelperText-root": {
            color: "var(--red-900)",
            marginLeft: 0,
          },
          "& svg.MuiSelect-icon": {
            display: "none", // Hide default if custom icon provided
          },
        }}
        InputProps={{
          startAdornment: startIconName && (
            <InputAdornment position="start">
              {renderIcon(startIconName)}
            </InputAdornment>
          ),
          // if the data is fetching from API -> then show loading icon, otherwise show dropdown icon
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                if (!disabled) setOpen((prev) => !prev); // toggle dropdown
              }}
            >
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <LuChevronDown size={20} color={iconColor} />
              )}
            </InputAdornment>
          ),
        }}
        SelectProps={{
          open,
          onOpen: () => setOpen(true),
          onClose: () => setOpen(false),
          displayEmpty: true,
          renderValue: (selected) => {
             if (selected === null || selected === undefined || selected === "") {
              return (
                <span
                  style={{
                    color: "var(--gray-500)",
                    marginLeft: startIconName ? "-15px" : "0px",
                  }}
                >
                  {placeholder || "Select..."}
                </span>
              );
            }
            const selectedOption = options.find(
              (opt) => opt.value === selected
            );


            renderValue?.(selectedOption?.raw);
              if (selected === true) return "Yes";
             if (selected === false) return "No";
            return selectedOption?.label || selectedOption?.id || selected;
          },
          IconComponent: dropdownIconName ? () => null : undefined,
          MenuProps: {
            PaperProps: {
              sx: {
                maxHeight: 300,
                minWidth: 200,
                width: 200,
                overflow: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 12px var(--gray-970)",
                border: "1px solid var(--gray-300)",
                "& .MuiMenuItem-root": {
                  borderRadius: "5px",
                  fontSize: "14px",
                  width: "auto",
                  mx: 1,
          
                  // 🟢 Enable text wrapping:
                  whiteSpace: "normal", // allows multiple lines
                  wordBreak: "break-word", // breaks long words properly
                  overflowWrap: "break-word", // ensures proper wrapping
                  lineHeight: 1.4, // adds spacing between wrapped lines
          
                  "&:hover": {
                    backgroundColor: "var(--brand-100)",
                  },
                  "&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible": {
                    backgroundColor: "var(--brand-100)",
                  },
                },
              },
            },     
          },
        }}
        {...props}
      >
        {Array.isArray(options) && options.length === 0 ? (
          <MenuItem disabled value="">
            No data available
          </MenuItem>
        ) : (
          Array.isArray(options) &&
          options.map((opt, idx) => (
            <MenuItem
              key={idx}
              value={opt.value ?? opt.name ?? opt.id}
              sx={{ m: "5px" }}
              disabled={opt.disabled === true}
            >
              {opt.label ?? opt.name ?? String(opt)}
            </MenuItem>
          ))
        )}
      </TextField>
    </Box>
  );
});
